// src/modules/competition/components/JoinTeamModal/index.js
import React, { useState } from 'react';
import { Modal, Button, Form, ListGroup, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { DEFAULT_TEAM_ICON } from '../TeamManager';

const JoinTeamModal = ({ show, onHide, teams, onCreateTeam, onJoinTeam, isLoading, canCreateTeam }) => {
    const [view, setView] = useState('pick'); // 'pick' | 'create'
    const [newTeamName, setNewTeamName] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newTeamName.trim()) return;
        await onCreateTeam(newTeamName.trim());
        setNewTeamName('');
    };

    const handleHide = () => {
        setView('pick');
        setNewTeamName('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-people me-2"></i>
                    Join a Team
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {view === 'pick' && (
                    <>
                        {teams.length > 0 ? (
                            <>
                                <p className="text-muted mb-3">
                                    {canCreateTeam
                                        ? 'Select an existing team or create a new one.'
                                        : 'Select a team to join.'}
                                </p>
                                <ListGroup className="mb-3">
                                    {teams.map(team => (
                                        <ListGroup.Item
                                            key={team.teamId}
                                            className="d-flex justify-content-between align-items-center"
                                            action
                                            onClick={() => onJoinTeam(team.teamId)}
                                            disabled={isLoading}
                                        >
                                            <span>
                                                <span className="me-2" style={{ fontSize: '1.1rem' }}>
                                                    {team.icon || DEFAULT_TEAM_ICON}
                                                </span>
                                                {team.name}
                                            </span>
                                            <Badge bg="secondary">
                                                {team.members?.length ?? 0} member{team.members?.length !== 1 ? 's' : ''}
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </>
                        ) : (
                            <p className="text-muted mb-3">
                                {canCreateTeam
                                    ? 'No teams yet — be the first to create one!'
                                    : 'No teams have been created yet. Ask an admin to add teams.'}
                            </p>
                        )}
                        {canCreateTeam && (
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => setView('create')}
                                disabled={isLoading}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create New Team
                            </Button>
                        )}
                    </>
                )}

                {view === 'create' && (
                    <Form onSubmit={handleCreate}>
                        <Form.Group className="mb-3">
                            <Form.Label>Team Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={newTeamName}
                                onChange={e => setNewTeamName(e.target.value)}
                                placeholder="Enter team name"
                                maxLength={50}
                                autoFocus
                                required
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isLoading || !newTeamName.trim()}
                                className="flex-grow-1"
                            >
                                {isLoading ? 'Creating…' : 'Create & Join'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={() => setView('pick')}
                                disabled={isLoading}
                            >
                                Back
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

JoinTeamModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    teams: PropTypes.arrayOf(PropTypes.shape({
        teamId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        members: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
    onCreateTeam: PropTypes.func.isRequired,
    onJoinTeam: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    canCreateTeam: PropTypes.bool.isRequired,
};

export default JoinTeamModal;
